import { ClientApplicationStatus } from 'api/models/ClientApplicationStatus';
import { FEATURE } from 'constant/features';
import { Role } from 'data/constants/roles';
import type {
    ApplicationRetainer,
    ApplicationRetainerStatus,
    ApplicationRetainersList,
    CaseRetainerType,
    CreateRetainerFieldVisibility,
    RetainerCreateState,
    RetainerFlow,
    RetainerFlowStep,
    RetainerFlowStepKey,
} from 'types';
import { isAgreementSigningBlocked } from 'utils/agreement';

export const RETAINER_FLOW_FEATURES: readonly RetainerFlow[] = [
    FEATURE.RETAINER_FLOW_LEGACY_SERVICED_OR_NON_SERVICED,
    FEATURE.RETAINER_FLOW_TWO_STAGE_SERVICE_THEN_CONSULTANT_SERVICED_AGREEMENT,
    FEATURE.RETAINER_FLOW_SERVICED_OR_NON_SERVICED,
    FEATURE.RETAINER_FLOW_FREE,
];

export const CASE_RETAINER_TYPES: readonly CaseRetainerType[] = [
    'visa-application',
    'service-agreement',
    'eligibility',
    'milestones',
    'engagement',
];

export const CASE_RETAINER_TYPE_LABEL_KEYS: Record<CaseRetainerType, string> = {
    'visa-application': 'retainerTypeVisaApplication',
    'service-agreement': 'retainerTypeServiceAgreement',
    eligibility: 'retainerTypeEligibility',
    milestones: 'retainerTypeMilestones',
    engagement: 'retainerTypeEngagement',
};

/** Locale key for a retainer type; `null`/unknown means a legacy PDF retainer. */
export const getRetainerTypeLabelKey = (type: string | null | undefined): string =>
    isCaseRetainerType(type) ? CASE_RETAINER_TYPE_LABEL_KEYS[type] : 'retainerTypeLegacy';

export const CLIENT_STATUS_LABEL_KEYS: Record<ClientApplicationStatus, string> = {
    [ClientApplicationStatus.ASSESSMENT]: 'assessment',
    [ClientApplicationStatus.NOT_ASSIGNED]: 'notAssigned',
    [ClientApplicationStatus.CLIENT_WITH_ISSUES]: 'clientWithIssues',
    [ClientApplicationStatus.PROCESSING]: 'processing',
    [ClientApplicationStatus.IN_REVIEW]: 'inReview',
    [ClientApplicationStatus.READY_FOR_SUBMISSION]: 'readyForSubmission',
    [ClientApplicationStatus.AWAITING_ITA]: 'awaitingITA',
    [ClientApplicationStatus.SUBMITTED]: 'submitted',
    [ClientApplicationStatus.VISA_RESULTS]: 'visaResults',
    [ClientApplicationStatus.AWAITING_LOA]: 'awaitingLOA',
    [ClientApplicationStatus.CLOSED]: 'closed',
    [ClientApplicationStatus.ON_HOLD]: 'onHold',
    [ClientApplicationStatus.REMOVED]: 'removed',
};

export const getClientStatusLabelKey = (status: string | null | undefined): string | null =>
    status && status in CLIENT_STATUS_LABEL_KEYS
        ? CLIENT_STATUS_LABEL_KEYS[status as ClientApplicationStatus]
        : null;

export const isRetainerFlow = (value: string | null | undefined): value is RetainerFlow =>
    !!value && (RETAINER_FLOW_FEATURES as readonly string[]).includes(value);

export const isCaseRetainerType = (value: string | null | undefined): value is CaseRetainerType =>
    !!value && (CASE_RETAINER_TYPES as readonly string[]).includes(value);

/**
 * Template flows use `/application/:id/retainers`; legacy keeps the PDF UI.
 * `null`/`undefined` means no list has loaded yet, which renders as legacy too.
 */
export const isTemplateRetainerFlow = (flow: RetainerFlow | null | undefined): boolean =>
    flow != null && flow !== FEATURE.RETAINER_FLOW_LEGACY_SERVICED_OR_NON_SERVICED;

/**
 * `/me` may include at most one RETAINER_FLOW_*. SUPER_SUPER_ADMIN gets the
 * full catalog, so never treat `/me` as the case-level flow. Legacy is the
 * default, so an explicit legacy entry means the same as none.
 */
export function pickRetainerFlowFromMe(
    enabledFeatures: readonly string[] | null | undefined,
    role: string | null | undefined
): RetainerFlow | null {
    if (role === Role.SUPER_SUPER_ADMIN || !Array.isArray(enabledFeatures)) {
        return null;
    }

    const found = RETAINER_FLOW_FEATURES.filter(
        (feature) => isTemplateRetainerFlow(feature) && enabledFeatures.includes(feature)
    );
    return found.length === 1 ? found[0] : null;
}

/**
 * Only consulted when the client has no case yet (so there is no list to read
 * the flow from). SUPER_SUPER_ADMIN always gets the template UI so the first
 * case's list can decide.
 */
export function shouldUseTemplateRetainerCreate(
    enabledFeatures: readonly string[] | null | undefined,
    role: string | null | undefined
): boolean {
    if (role === Role.SUPER_SUPER_ADMIN) {
        return true;
    }
    return pickRetainerFlowFromMe(enabledFeatures, role) != null;
}

/** All of a client's cases belong to one tenant, so the first loaded list decides. */
export function resolveListFlow(
    lists: Array<ApplicationRetainersList | null | undefined>
): RetainerFlow | null {
    return lists.find((list) => list?.flow)?.flow ?? null;
}

export const getRetainerStatus = (retainer: ApplicationRetainer): ApplicationRetainerStatus => {
    if (retainer.signed) {
        return 'signed';
    }
    return retainer.sent_at ? 'sent' : 'draft';
};

export const areAllUnsignedRetainersSigningBlocked = (
    retainers: readonly ApplicationRetainer[]
): boolean => {
    const unsignedRetainers = retainers.filter((retainer) => !retainer.signed);
    return (
        unsignedRetainers.length > 0 &&
        unsignedRetainers.every((retainer) => isAgreementSigningBlocked(retainer))
    );
};

/** Oldest first, so a service-agreement always renders above its visa-application. */
export function sortRetainersOldestFirst(
    retainers: readonly ApplicationRetainer[]
): ApplicationRetainer[] {
    return [...retainers].sort((a, b) => {
        const aTime = a.created_at ? Date.parse(a.created_at) : NaN;
        const bTime = b.created_at ? Date.parse(b.created_at) : NaN;
        if (!Number.isNaN(aTime) && !Number.isNaN(bTime) && aTime !== bTime) {
            return aTime - bTime;
        }
        return a.agreement_id - b.agreement_id;
    });
}

export const findRetainerOfType = (
    retainers: readonly ApplicationRetainer[] | undefined,
    type: CaseRetainerType
): ApplicationRetainer | undefined =>
    retainers?.find((retainer) => retainer.agreement_type === type);

export const hasSignedRetainerOfType = (
    list: ApplicationRetainersList | null | undefined,
    type: CaseRetainerType
): boolean => Boolean(list?.retainers.some((r) => r.agreement_type === type && r.signed));

/**
 * TWO_STAGE: the server generates the visa retainer once every document is
 * preapproved and the case is In review, so nothing can be created meanwhile.
 */
export const isVisaRetainerPending = (list: ApplicationRetainersList | null | undefined): boolean =>
    list?.flow === FEATURE.RETAINER_FLOW_TWO_STAGE_SERVICE_THEN_CONSULTANT_SERVICED_AGREEMENT &&
    hasSignedRetainerOfType(list, 'service-agreement') &&
    !findRetainerOfType(list.retainers, 'visa-application');

/**
 * Offer is locked when a signed visa-application exists, or a signed legacy
 * PDF with no type. A signed service-agreement does not lock the offer.
 */
export function isOfferLocked(list: ApplicationRetainersList | null | undefined): boolean {
    if (!list?.retainers?.length) {
        return false;
    }

    return list.retainers.some((retainer) => {
        if (!retainer.signed) {
            return false;
        }
        if (retainer.agreement_type === 'visa-application') {
            return true;
        }
        if (
            list.flow === FEATURE.RETAINER_FLOW_LEGACY_SERVICED_OR_NON_SERVICED &&
            !retainer.agreement_type
        ) {
            return true;
        }
        return false;
    });
}

/**
 * The page is per-case, so no case picker: a visa-application is always a new
 * row on the same application. Type/agent choice only exists on FREE, where
 * consultants may assign themselves or a colleague.
 */
export function getCreateRetainerFields({
    flow,
    allowedTypes,
}: {
    flow: RetainerFlow;
    allowedTypes: readonly string[];
}): CreateRetainerFieldVisibility {
    const isFree = flow === FEATURE.RETAINER_FLOW_FREE;
    const lockedType = isFree ? null : (allowedTypes.find(isCaseRetainerType) ?? null);

    return {
        showType: isFree,
        showAgent: isFree,
        lockedType,
    };
}

/**
 * Derives the "Create retainer" control from the list payload plus the offer.
 * The API mirrors these rules (400 without an offer, 409 for a forbidden type),
 * so this only decides copy and whether the button is enabled.
 */
export function getRetainerCreateState({
    list,
    milestonesCount,
}: {
    list: ApplicationRetainersList | null | undefined;
    milestonesCount: number;
}): RetainerCreateState {
    const flow = list?.flow ?? null;
    const allowedTypes = list?.allowed_types ?? [];
    const retainers = list?.retainers ?? [];

    if (milestonesCount === 0) {
        return { canCreate: false, reason: 'createOfferFirst' };
    }

    if (allowedTypes.length > 0) {
        return { canCreate: true, reason: null };
    }

    if (flow === FEATURE.RETAINER_FLOW_TWO_STAGE_SERVICE_THEN_CONSULTANT_SERVICED_AGREEMENT) {
        // Only the service agreement is created here; the visa retainer is server-generated
        const serviceAgreement = findRetainerOfType(retainers, 'service-agreement');
        if (serviceAgreement && !serviceAgreement.signed) {
            return { canCreate: false, reason: 'waitingForServiceAgreementSignature' };
        }
        if (serviceAgreement?.signed) {
            return {
                canCreate: false,
                reason: isVisaRetainerPending(list)
                    ? 'visaRetainerAutoGenerated'
                    : 'allRetainerTypesCreated',
            };
        }
    }

    if (flow === FEATURE.RETAINER_FLOW_SERVICED_OR_NON_SERVICED) {
        return { canCreate: false, reason: 'caseAlreadyHasRetainer' };
    }

    if (flow === FEATURE.RETAINER_FLOW_FREE) {
        return { canCreate: false, reason: 'allRetainerTypesCreated' };
    }

    // Legacy (or list not loaded): nothing is created from this UI
    return { canCreate: false, reason: 'noAllowedRetainerTypes' };
}

const STEP_KEYS_BY_FLOW: Record<RetainerFlow, readonly RetainerFlowStepKey[]> = {
    [FEATURE.RETAINER_FLOW_LEGACY_SERVICED_OR_NON_SERVICED]: [],
    [FEATURE.RETAINER_FLOW_TWO_STAGE_SERVICE_THEN_CONSULTANT_SERVICED_AGREEMENT]: [
        'case',
        'offer',
        'serviceAgreement',
        'visaRetainer',
    ],
    [FEATURE.RETAINER_FLOW_SERVICED_OR_NON_SERVICED]: ['case', 'offer', 'retainer'],
    [FEATURE.RETAINER_FLOW_FREE]: ['case', 'offer', 'retainers'],
};

/** Stepper model per flow; empty for legacy. */
export function getRetainerFlowSteps({
    list,
    milestonesCount,
}: {
    list: ApplicationRetainersList | null | undefined;
    milestonesCount: number;
}): RetainerFlowStep[] {
    if (!list) {
        return [];
    }
    const { flow, retainers } = list;

    const completedByKey: Record<RetainerFlowStepKey, boolean> = {
        case: true,
        offer: milestonesCount > 0,
        serviceAgreement: hasSignedRetainerOfType(list, 'service-agreement'),
        visaRetainer: hasSignedRetainerOfType(list, 'visa-application'),
        retainer: retainers.some((retainer) => retainer.signed),
        retainers: retainers.length > 0 && list.allowed_types.length === 0,
    };

    return STEP_KEYS_BY_FLOW[flow].map((key) => ({ key, completed: completedByKey[key] }));
}

/** Index of the first incomplete step (or the last step when everything is done). */
export const getActiveStepIndex = (steps: readonly RetainerFlowStep[]): number => {
    const index = steps.findIndex((step) => !step.completed);
    return index === -1 ? Math.max(steps.length - 1, 0) : index;
};
